import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Snowflake, Package, Calendar, AlertTriangle, Loader2 } from 'lucide-react';
import { format, differenceInDays, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';

export interface MaterialLot {
  id: string;
  material_id: string;
  batch_number: string | null;
  roll_number: string | null;
  quantity: number;
  square_meters: number | null;
  square_meters_used: number | null;
  production_date: string | null;
  expiry_date: string | null;
  is_active: boolean;
  is_in_use: boolean;
}

export interface FreezerMaterial {
  id: string;
  name: string;
  code: string | null;
  category: string;
  material_type: string | null;
  current_stock: number;
  unit: string;
  supplier: string | null;
  is_freezer_material: boolean;
  lots: MaterialLot[];
}

export interface SelectedMaterial {
  material: FreezerMaterial;
  lot: MaterialLot;
  availableSquareMeters: number;
}

interface FreezerMaterialSelectorProps {
  value: SelectedMaterial | null;
  onChange: (selection: SelectedMaterial | null) => void;
  disabled?: boolean;
}

const materialCategories = [
  { value: 'all', label: 'Todos' },
  { value: 'fiber', label: 'Fibra' },
  { value: 'fabric', label: 'Tejido' },
  { value: 'resin', label: 'Resina' },
  { value: 'prepreg', label: 'Prepreg' },
  { value: 'other', label: 'Otro' },
];

export function FreezerMaterialSelector({
  value,
  onChange,
  disabled = false,
}: FreezerMaterialSelectorProps) {
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [onlyAvailable, setOnlyAvailable] = useState(true);

  // Fetch freezer materials with their lots
  const { data: materials = [], isLoading } = useQuery({
    queryKey: ['freezer-materials-with-lots'],
    queryFn: async () => {
      // Get materials marked as freezer materials with stock > 0
      const { data: matData, error: matError } = await api
        .from('material_inventory')
        .select('id, name, code, category, material_type, current_stock, unit, supplier, is_freezer_material')
        .eq('is_freezer_material', true)
        .eq('is_active', true)
        .gt('current_stock', 0)
        .order('name');

      if (matError) throw matError;
      if (!matData || matData.length === 0) return [];

      // Get all lots for these materials
      const materialIds = matData.map(m => m.id);
      const { data: lotsData, error: lotsError } = await api
        .from('material_lots')
        .select('*')
        .in('material_id', materialIds)
        .eq('is_active', true)
        .order('expiry_date', { ascending: true });

      if (lotsError) throw lotsError;

      // Combine materials with their lots
      const materialsWithLots: FreezerMaterial[] = matData.map(mat => ({
        ...mat,
        lots: (lotsData || []).filter(lot => lot.material_id === mat.id),
      }));

      return materialsWithLots;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Filter materials based on search and category
  const filteredMaterials = useMemo(() => {
    return materials.filter(mat => {
      // Category filter
      if (categoryFilter !== 'all' && mat.category !== categoryFilter) {
        return false;
      }

      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesName = mat.name.toLowerCase().includes(search);
        const matchesCode = mat.code?.toLowerCase().includes(search);
        const matchesSupplier = mat.supplier?.toLowerCase().includes(search);
        if (!matchesName && !matchesCode && !matchesSupplier) {
          return false;
        }
      }

      // Only available filter - at least one lot with available square meters
      if (onlyAvailable) {
        const hasAvailableLot = mat.lots.some(lot => {
          const available = (lot.square_meters || lot.quantity || 0) - (lot.square_meters_used || 0);
          return available > 0;
        });
        if (!hasAvailableLot) return false;
      }

      return true;
    });
  }, [materials, categoryFilter, searchTerm, onlyAvailable]);

  // Calculate available square meters for a lot
  const getAvailableSquareMeters = (lot: MaterialLot): number => {
    const total = lot.square_meters || lot.quantity || 0;
    const used = lot.square_meters_used || 0;
    return Math.max(0, total - used);
  };

  // Check if lot is expiring soon (within 30 days)
  const isExpiringSoon = (expiryDate: string | null): boolean => {
    if (!expiryDate) return false;
    const days = differenceInDays(parseISO(expiryDate), new Date());
    return days >= 0 && days <= 30;
  };

  // Check if lot is expired
  const isExpired = (expiryDate: string | null): boolean => {
    if (!expiryDate) return false;
    return differenceInDays(parseISO(expiryDate), new Date()) < 0;
  };

  const handleSelectLot = (material: FreezerMaterial, lot: MaterialLot) => {
    if (disabled) return;

    const availableSquareMeters = getAvailableSquareMeters(lot);
    onChange({
      material,
      lot,
      availableSquareMeters,
    });
  };

  const isSelected = (lotId: string): boolean => {
    return value?.lot.id === lotId;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Cargando materiales...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn(disabled && "opacity-60")}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Snowflake className="w-5 h-5 text-blue-500" />
          Material de Congelador
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[150px]">
            <Label className="text-xs">Buscar</Label>
            <Input
              placeholder="Nombre, código..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={disabled}
              className="h-9"
            />
          </div>
          <div className="w-[140px]">
            <Label className="text-xs">Categoría</Label>
            <Select value={categoryFilter} onValueChange={setCategoryFilter} disabled={disabled}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {materialCategories.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 h-9">
            <Checkbox
              id="only-available"
              checked={onlyAvailable}
              onCheckedChange={(checked) => setOnlyAvailable(checked as boolean)}
              disabled={disabled}
            />
            <Label htmlFor="only-available" className="text-xs cursor-pointer">
              Solo disponibles
            </Label>
          </div>
        </div>

        {/* Materials List */}
        <ScrollArea className="h-[280px] border rounded-md">
          {filteredMaterials.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <Package className="w-8 h-8 mb-2" />
              <p className="text-sm">No hay materiales disponibles</p>
            </div>
          ) : (
            <RadioGroup value={value?.lot.id || ''} className="p-2 space-y-2">
              {filteredMaterials.map(material => (
                <div key={material.id} className="space-y-1">
                  {/* Material Header */}
                  <div className="flex items-center gap-2 px-2 py-1 bg-muted/50 rounded text-sm font-medium">
                    <Package className="w-4 h-4" />
                    {material.name}
                    {material.code && (
                      <Badge variant="outline" className="text-xs">{material.code}</Badge>
                    )}
                    <span className="text-xs text-muted-foreground ml-auto">
                      {material.supplier}
                    </span>
                  </div>

                  {/* Lots */}
                  {material.lots.length === 0 ? (
                    <div className="px-4 py-2 text-xs text-muted-foreground italic">
                      Sin lotes registrados
                    </div>
                  ) : (
                    material.lots.map(lot => {
                      const available = getAvailableSquareMeters(lot);
                      const expiringSoon = isExpiringSoon(lot.expiry_date);
                      const expired = isExpired(lot.expiry_date);
                      const isDisabled = disabled || available <= 0 || expired;

                      return (
                        <div
                          key={lot.id}
                          className={cn(
                            "flex items-center gap-3 px-3 py-2 rounded-md border cursor-pointer transition-colors",
                            isSelected(lot.id) && "border-primary bg-primary/5",
                            !isSelected(lot.id) && !isDisabled && "hover:bg-muted/50",
                            isDisabled && "opacity-50 cursor-not-allowed bg-muted/30"
                          )}
                          onClick={() => !isDisabled && handleSelectLot(material, lot)}
                        >
                          <RadioGroupItem
                            value={lot.id}
                            disabled={isDisabled}
                            className="shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 text-sm">
                              <span className="font-medium">
                                Lote: {lot.batch_number || 'N/A'}
                              </span>
                              {lot.roll_number && (
                                <span className="text-muted-foreground">
                                  Rollo: {lot.roll_number}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                              <span className={cn(
                                "font-medium",
                                available <= 5 && "text-orange-500",
                                available <= 0 && "text-red-500"
                              )}>
                                {available.toFixed(2)} m² disponible
                              </span>
                              {lot.expiry_date && (
                                <span className={cn(
                                  "flex items-center gap-1",
                                  expiringSoon && "text-orange-500",
                                  expired && "text-red-500"
                                )}>
                                  <Calendar className="w-3 h-3" />
                                  {expired ? 'Vencido' : format(parseISO(lot.expiry_date), 'dd/MM/yyyy', { locale: es })}
                                  {expiringSoon && !expired && (
                                    <AlertTriangle className="w-3 h-3" />
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              ))}
            </RadioGroup>
          )}
        </ScrollArea>

        {/* Selected Material Info */}
        {value && (
          <div className="p-3 bg-primary/10 rounded-md border border-primary/30">
            <div className="flex items-center gap-2 text-sm">
              <Snowflake className="w-4 h-4 text-primary" />
              <span className="font-medium text-primary">Material seleccionado:</span>
            </div>
            <div className="mt-1 text-sm">
              <span className="font-medium">{value.material.name}</span>
              <span className="text-muted-foreground"> — Lote: {value.lot.batch_number || 'N/A'}</span>
              {value.lot.roll_number && (
                <span className="text-muted-foreground">, Rollo: {value.lot.roll_number}</span>
              )}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              Disponible: <span className="font-medium text-foreground">{value.availableSquareMeters.toFixed(2)} m²</span>
              {value.lot.expiry_date && (
                <> | Vence: {format(parseISO(value.lot.expiry_date), 'dd/MM/yyyy', { locale: es })}</>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
